import { CryptoGuess } from "@/event-core/types";
import { formatLocalDate, getLocalTimezoneLabel } from "@/utils/formatLocalDate";
import { get } from "@/utils/utils";
import { useMemo, useState } from "react";
import { fetchCryptoGuessPage } from "../api";

export function useTimelinePopover( eventNo: string, direction: boolean, initialId: number ) {
    const [ guesses, setGuesses ] = useState<CryptoGuess[]>( [] );
    const [ loading, setLoading ] = useState( false );
    const [ hasMore, setHasMore ] = useState( true );

    const handleLoad = async () => {
        if ( loading || !initialId ) return;

        setLoading( true );
        try {
            const result = await fetchCryptoGuessPage( { eventNo, direction, id: initialId, size: 20 } );

            setGuesses( result.guessList );
            setHasMore( result.hasMore );
        } catch ( error ) {
            console.error( "Failed to load guesses:", error );
        } finally {
            setLoading( false );
        }
    };

    const handleLoadMore = async () => {
        if ( loading || !hasMore || guesses.length === 0 ) return;

        setLoading( true );
        try {
            const lastGuess = guesses[guesses.length - 1];
            const lastId = Number( get( lastGuess, "id", 0 ) );

            const result = await fetchCryptoGuessPage( { eventNo, direction, id: lastId, size: 20 } );

            setGuesses( ( prev ) => [ ...prev, ...result.guessList ] );
            setHasMore( result.hasMore );
        } catch ( error ) {
            console.error( "Failed to load more guesses:", error );
        } finally {
            setLoading( false );
        }
    };

    const handleScroll = ( e: React.UIEvent<HTMLDivElement> ) => {
        const target = e.currentTarget;
        const bottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 10;
        if ( bottom ) {
            handleLoadMore();
        }
    };

    const options = useMemo( () => {
        return guesses.map( ( guess: CryptoGuess ) => {
            const scheduleStartTime = get( guess, "scheduleStartTime", "" );
            const label = `${formatLocalDate( scheduleStartTime, "hh:mm A" )} ${
                getLocalTimezoneLabel( scheduleStartTime )
            }`;
            return { label, value: get( guess, "guessNo", "" ), guess };
        } );
    }, [ guesses ] );

    return { guesses, loading, options, handleLoad, handleScroll };
}
